import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { STALE_TIME } from "../types/staleTime"
import { convertTimelapse, convertTimelapses, Timelapse, TimelapseCreateSchema, TimelapseFilter, TimelapseUpdateSchema } from "../types/timelapse"
import { apiDelete, apiGet, apiPost, apiPut } from "./query"

const ENDPOINT = "timelapse"
const PAGE_LIMIT = 100;

export const useTimelapseGet = ({ id }: Pick<Timelapse, "id">) => {
  return useQuery({
    queryKey: ["timelapse", id],
    queryFn: async () => (await apiGet(`${ENDPOINT}/${id}`, convertTimelapse)).data,
    staleTime: STALE_TIME.MIN_5,
    throwOnError: true,
    enabled: id > 0,
  })
}

export const useTimelapseGetAll = (filter?: TimelapseFilter) => {
  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage, error, refetch, isFetching } = useInfiniteQuery({
    queryKey: ["timelapse", "deleted", JSON.stringify(filter)],
    queryFn: async ({ pageParam = 0 }) => {
      const queryParams = new URLSearchParams({
        page: pageParam.toString(),
        limit: PAGE_LIMIT.toString(),
      });

      if (filter?.status) queryParams.append("status", String(filter.status))
      if (filter?.scanner) queryParams.append("scanner", filter.scanner.toString())
      if (filter?.deleted) queryParams.append("deleted", String(filter.deleted))

      const url = `${ENDPOINT}?${queryParams.toString()}`;

      return (await apiGet(url, convertTimelapses)).data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length < PAGE_LIMIT ? undefined : allPages.length;
    },
    staleTime: STALE_TIME.MIN_5,
    throwOnError: true,
  })

  const timelapses = data?.pages.flat() ?? [];

  return {
    data: timelapses,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    error,
    refetch,
    isFetching,
  }
}

export const useTimelapseCreate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (timelapse: TimelapseCreateSchema) => apiPut(ENDPOINT, timelapse),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timelapse"] })
      queryClient.invalidateQueries({ queryKey: ["scanner"] })
    },
  })
}

export const useTimelapseUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (timelapse: TimelapseUpdateSchema) => apiPost(`${ENDPOINT}/${timelapse.id}`, timelapse),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["timelapse"] })
      queryClient.invalidateQueries({ queryKey: ["scanner"] })
    },
  })
}

export const useTimelapseDelete = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: Pick<Timelapse, "id">) => apiDelete(`${ENDPOINT}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["timelapse"] }),
  })
}

export const useTimelapseUndelete = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: Pick<Timelapse, "id">) => apiPost(`${ENDPOINT}/deleted/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["timelapse"] }),
  })
}
